import React, { useState } from "react";
import { Copy, ExternalLink, Loader2, Share2 } from "lucide-react";
import { toast } from "sonner";
import { createReadinessShare } from "@/lib/api";
import { track, EVENTS } from "@/lib/analytics";
import { errorMessage } from "@/lib/errorMessage";

export default function AdvisorSharePanel({ workbook }) {
  const [creating, setCreating] = useState(false);
  const [shareUrl, setShareUrl] = useState("");

  const create = async () => {
    setCreating(true);
    try {
      const r = await createReadinessShare(workbook);
      const origin = window.location.origin;
      const url = `${origin}/workbook-view/${r.share_id}`;
      setShareUrl(url);
      track(EVENTS.CALCULATOR_RUN, { workbook_share_link: true });
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Link created and copied to your clipboard.");
      } catch {
        toast.success("Link created. Copy it from the box below.");
      }
    } catch (err) {
      toast.error(errorMessage(err, "Could not create the share link."));
    } finally {
      setCreating(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Copied.");
    } catch {
      toast.error("Could not copy — long-press the link to select.");
    }
  };

  return (
    <section
      data-testid="advisor-share"
      className="mt-6 bg-white border border-[#E5E2DC] rounded-2xl p-5 sm:p-6 print:hidden"
    >
      <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#2B5339] mb-2 inline-flex items-center gap-1.5">
        <Share2 size={11} /> Share with an advisor
      </div>
      <p className="text-sm text-[#1C2B23] leading-relaxed max-w-2xl">
        Generate a read-only link an advisor, spouse, or family member can open in a browser. No
        signup needed for them. The link opens a clean view of everything you filled in.
      </p>
      {!shareUrl ? (
        <button
          data-testid="advisor-share-create"
          type="button"
          onClick={create}
          disabled={creating}
          className="mt-4 inline-flex items-center gap-2 bg-[#2B5339] text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-[#1C2B23] disabled:opacity-50"
        >
          {creating && <Loader2 size={13} className="animate-spin" />}
          Get an advisor share link
        </button>
      ) : (
        <div className="mt-4 space-y-3">
          <div className="flex gap-2">
            <input
              data-testid="advisor-share-url"
              readOnly
              value={shareUrl}
              className="flex-1 bg-[#F9F8F6] border border-[#E5E2DC] rounded-lg px-3 py-2 text-sm font-mono text-[#1C2B23] focus:outline-none focus:border-[#2B5339]"
              onFocus={(e) => e.currentTarget.select()}
            />
            <button
              data-testid="advisor-share-copy"
              type="button"
              onClick={copy}
              className="inline-flex items-center gap-1.5 bg-white border border-[#E5E2DC] rounded-lg px-3 text-sm text-[#5C6B62] hover:border-[#2B5339]"
            >
              <Copy size={13} /> Copy
            </button>
            <a
              data-testid="advisor-share-open"
              href={shareUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 bg-white border border-[#E5E2DC] rounded-lg px-3 text-sm text-[#5C6B62] hover:border-[#2B5339]"
            >
              <ExternalLink size={13} /> Open
            </a>
          </div>
          <p className="text-[11px] text-[#5C6B62] leading-relaxed">
            Anyone with this link can view your workbook (read-only). It cannot be edited from the
            link. Share only with people you trust.
          </p>
        </div>
      )}
    </section>
  );
}
